import React, {useState, useEffect} from 'react';
import queryString from 'query-string';

const useBulkActions = (items) => {

  // There are three possible values for selectionMode: all, exclude, include
  const [selectionMode, setSelectionMode] = useState('include');

  // The list of Ids that are included (include) or excluded (exclude) for bulk actions.
  const [trackedItems, setTrackedItems] = useState([]);

  // Track if the "select all" prompt should display
  const [promptSelectAll, setPromptSelectAll] = useState(false);

  /**
   * Add passed contact IDs to the list of tracked IDs. Triggers a tracking mode switch if necessary.
   *
   * @param {string[]}  The list of object IDs to add to tracking
   * @param {Object} [options]
   * @param {boolean} options.promptSelectAll Whether to prompt for "select all" after this
   */
  const addTrackedItems = (ids, options = {}) => {
    let trackedIds;
    let mode = selectionMode;

    if (mode === 'all') {
      // In all mode all contacts are selected; can only uncheck to begin 'exclude' mode tracking
      trackedIds = ids;
      mode = 'exclude';
    } else {
      trackedIds = [...new Set(trackedItems.concat(ids))];

      // If tracking all IDs triggers a selectionMode switch and stops tracking IDs
      if (trackedIds.length === items.total) {
        mode = mode === 'exclude' ? 'include' : 'all';
        trackedIds = [];
      }
    }
    // Update state
    setTrackedItems(trackedIds);
    setSelectionMode(mode);
    setPromptSelectAll(mode !== 'all' && options.promptSelectAll);
  };

  /**
   * Remove passed contact IDs from the list of tracked IDs. Triggers a selectionMode switch if necessary.
   *
   * @param {string[]} contactIds The list of contact IDs to remove from tracking
   * @param {Object} [options]
   * @param {boolean} options.promptSelectAll Whether to prompt for "select all" after this
   */
  const removeTrackedItems = (ids, options = {}) => {
    const trackedIds = trackedItems.filter(id => !ids.includes(id));
    let mode = selectionMode;

    // In exclude mode, if not tracking any IDs, switch to all mode
    if (trackedIds.length === 0 && mode === 'exclude') {
      mode = 'all';
    }

    // Update state
    setTrackedItems(trackedIds);
    setSelectionMode(mode);
    setPromptSelectAll(mode !== 'all' && options.promptSelectAll);
  };

  /**
   * Toggle whether a single contact is tracked for current selectionMode
   * @param e DOM event
   */
  const toggleTrackedItem = (e) => {
    const id = e.target.value;

    if (trackedItems.includes(id)) {
      removeTrackedItems([id]);
    } else {
      addTrackedItems([id]);
    }
  };

  /**
   * When toggled "on", selects all contacts for current selectionMode, i.e. add to includes or removes from excludes.
   * When toggled "off", sets selectionMode to 'include' with nothing selected.
   * @param e DOM event
   */
  const togglePageTracked = (e) => {
    if (e.target.checked) {
      // If checked, we need to add or remove the entire page from tracked contacts,
      // depending on the current mode.
      const pageItems = items.map(c => c.id);
      if (selectionMode === 'include') {
        addTrackedItems(pageItems, { promptSelectAll: true });
      } else { // selectionMode === 'exclude'
        removeTrackedItems(pageItems, { promptSelectAll: true });
      }
    } else {
      // If unchecked, stop tracking ALL contacts and switch to 'include' selectionMode.
      setTrackedItems([]);
      setPromptSelectAll(false);
      setSelectionMode('include');
    }
  };

  /**
   * Select all items, returning to all tracking/selectionMode
   * @param {MouseEvent} [e]
   */
  const selectAllItems = (e) => {
    if (e) {
      e.preventDefault();
    }
    setTrackedItems([]);
    setSelectionMode('all');
    setPromptSelectAll(false);
  };

  /**
   * Returns if a contact should be included for bulk actions
   * @param {string} studentId
   * @return {boolean} whether or not the contact is included
   */
  const itemIsSelected = (studentId) => {
    switch (selectionMode) {
      case 'exclude':
        return !trackedItems.includes(studentId);
      case 'include':
        return trackedItems.includes(studentId);
     case 'all':
        return true;
    }
  };

  /**
   * Calculates the number of contacts included for bulk actions, determined by selectionMode
   * @return {number|*} the number of contacts
   */
  const selectionModeCount = () => {
    switch (selectionMode) {
      case 'include':
        return trackedItems.length;
      case 'exclude':
        return items.total - trackedItems.length;
      case 'all':
        return items.total;
    }
  };

  useEffect(() => {
    // this indicates the filters on the page have changed, so we want to wipe tracking state and return to include mode
    setSelectionMode('include');
    setTrackedItems([]);
  }, [queryString.parse(location.search)['filters'], queryString.parse(location.search)['school_year_id']]);

  const isNoneSelected = selectionMode !== 'all' && !trackedItems.length;

  return {
    trackedItems,
    setTrackedItems,
    selectionModeCount,
    promptSelectAll,
    setPromptSelectAll,
    setSelectionMode,
    selectionMode,
    togglePageTracked,
    itemIsSelected,
    toggleTrackedItem,
    selectAllItems,

    // Metadata
    isAllSelected: selectionMode === 'all' || promptSelectAll,
    isAnySelected: !isNoneSelected,
    isNoneSelected
  }
};


export default useBulkActions;
